import { trace } from "@opentelemetry/api";
import { createMiddleware, createServerOnlyFn } from "@tanstack/react-start";
import { getCookie, getRequest } from "@tanstack/react-start/server";

import type { CoachSelect } from "~/../drizzle/schema";
import { COOKIE_NAMES, persistLocationIdCookie } from "~/lib/cookies";
import { setSentryUser } from "~/lib/server/sentry-context";

// Server-fn `.middleware([...])` lists keep this module in the client bundle.
// createServerOnlyFn severs the edge to the coach lookups in the client build.
const importCoachLookup = createServerOnlyFn(
  () => import("~/lib/server/coach"),
);

/** The signed-in Clerk user's coach row; a failed lookup counts as no coach. */
async function clerkCoachOrNull(): Promise<CoachSelect | null> {
  try {
    const { resolveCoachFromClerk } = await importCoachLookup();
    return (await resolveCoachFromClerk()).coach;
  } catch (e) {
    console.error("Coach auth via Clerk failed:", e);
    return null;
  }
}

function getSearchParams(): URLSearchParams {
  const request = getRequest();
  return new URL(request.url).searchParams;
}

/**
 * Clerk-only coach auth — for /my-coaching/* routes and server functions
 * that should always resolve the coach from the logged-in Clerk user.
 *
 * Use this for any function called from user-side routes. It ignores the
 * GHL locationId cookie entirely, preventing cross-account leakage.
 */
export const requireClerkCoachAuth = createMiddleware({
  type: "function",
}).server<{
  coach: CoachSelect;
}>(async ({ next }) => {
  const coach = await clerkCoachOrNull();
  if (coach) return next({ context: { coach } });

  // Throw a plain Error (not a Response) — server-fn responses are serialized
  // with seroval, which cannot round-trip a Response instance and would surface
  // an opaque "Seroval Error" on the client. The 403 in the message keeps
  // `isAuthError` (auth/auth-error.ts) able to detect this as an auth failure.
  throw new Error("No coach found (403)");
});

const importNovaFreshness = createServerOnlyFn(
  () => import("~/lib/server/nova/subscription/freshness"),
);

/**
 * Clerk coach auth for every NOVA server function: `context.coach` is the row
 * to decide NOVA access on. When her stored trial or period end has passed, or
 * her status is unresolved, it is re-read from Stripe and the database first,
 * so a page left open past the deadline cannot keep paid access, and a coach
 * who just paid is not refused from a cached row. Costs nothing otherwise.
 */
export const requireNovaCoachAuth = createMiddleware({ type: "function" })
  .middleware([requireClerkCoachAuth])
  .server(async ({ next, context }) => {
    const { freshCoachForNovaAccess } = await importNovaFreshness();
    return next({
      context: { coach: await freshCoachForNovaAccess(context.coach) },
    });
  });

/**
 * Coach auth with GHL fallback — Clerk-first, locationId-fallback.
 *
 * Used by server functions shared between user-side and GHL iframe routes.
 * Prefers the Clerk session (secure, authoritative) but falls back to
 * locationId for GHL iframes where the coach may not have a Clerk account yet.
 */
export const requireCoachAuth = createMiddleware({ type: "function" }).server<{
  coach: CoachSelect;
}>(async ({ next }) => {
  // Strategy 1: Clerk auth (preferred — secure, tied to logged-in user)
  const clerkCoach = await clerkCoachOrNull();
  if (clerkCoach) return next({ context: { coach: clerkCoach } });

  // Strategy 2: locationId from query params or cookie (GHL iframe fallback)
  const searchParams = getSearchParams();
  const queryLocationId =
    searchParams.get("locationId") || searchParams.get("location_id");
  const locationId =
    queryLocationId ?? getCookie(COOKIE_NAMES.locationId) ?? null;

  if (locationId) {
    try {
      const { getCoachByLocationId } = await importCoachLookup();
      const coach = await getCoachByLocationId(locationId);

      if (coach) {
        persistLocationIdCookie(locationId);
        trace.getActiveSpan()?.setAttribute("coachId", coach.id);
        setSentryUser({ id: coach.id, type: "coach" });
        return next({ context: { coach } });
      }
    } catch (e) {
      console.error("Coach auth via locationId failed:", e);
    }
  }

  // Plain Error rather than a Response — see requireClerkCoachAuth above.
  throw new Error("No coach found (403)");
});
